import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { GFFrame } from "@/components/GFFrame";

const PURPLE = "#4B006E";
const GENDERS = ["Lelaki", "Perempuan", "Tidak mahu nyatakan"];
const AGES = ["18–24", "25–34", "35–44", "45–54", "55+"];
const ROLES = ["Eksekutif / Staf", "Penyelia", "Pengurus", "Pengurus Kanan", "Pengurusan Atasan", "Lain-lain"];
const YEARS = ["Kurang 1 tahun", "1 – 3 tahun", "3 – 5 tahun", "5 – 10 tahun", "Lebih 10 tahun"];

const input = "w-full rounded-2xl border border-[#D8C4E4] bg-white px-4 py-3.5 text-[15px] text-[#4B006E] outline-none focus:border-[#4B006E] focus:ring-2 focus:ring-[#4B006E]/15";

const Field = ({ label, children }) => (
  <div className="space-y-2"><label className="text-sm font-semibold text-slate-600">{label}</label>{children}</div>
);
const Sel = ({ tid, v, on, ph, opts }) => (
  <select data-testid={tid} className={input} value={v} onChange={on}>
    <option value="">{ph}</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function CohortProfile() {
  const navigate = useNavigate();
  const [cohort, setCohort] = useState(null);
  const [f, setF] = useState({ nama: "", email: "", gender: "", age_range: "", role_level: "", department: "", years_org: "", years_role: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const c = sessionStorage.getItem("gf_cohort");
    if (!c) { navigate("/cohort"); return; }
    setCohort(JSON.parse(c));
  }, [navigate]);

  const submit = () => {
    const req = ["nama", "email", "gender", "age_range", "role_level", "years_org", "years_role"];
    if (req.some((k) => !f[k])) { toast.error("Sila lengkapkan maklumat yang diperlukan."); return; }
    sessionStorage.setItem("gf_cohort_profile", JSON.stringify(f));
    navigate("/cohort/soalan");
  };

  return (
    <GFFrame testId="cohort-profile-page">
      <div className="flex min-h-screen flex-col">
        <div className="px-6 pt-8">
          <button onClick={() => navigate("/cohort")} className="flex items-center gap-1 text-sm text-slate-400" data-testid="cohort-profile-back">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
        </div>
        <div className="flex flex-1 flex-col px-6 pb-10 pt-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mb-4 h-[3px] w-12 rounded-full bg-[#D4AF37]" />
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "#7A2E9E" }}>{cohort?.programme}</p>
            <h2 className="mt-1 font-heading text-3xl font-bold" style={{ color: PURPLE }}>Maklumat Peserta</h2>
            <p className="mt-2 text-sm text-slate-500">Maklumat ini digunakan untuk analisis kohort secara agregat sahaja.</p>
          </motion.div>

          <div className="mt-8 space-y-5">
            <Field label="Nama"><input data-testid="cohort-nama" className={input} placeholder="Nama penuh" value={f.nama} onChange={set("nama")} /></Field>
            <Field label="Emel"><input data-testid="cohort-email" type="email" className={input} placeholder="emel@organisasi.com" value={f.email} onChange={set("email")} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Jantina"><Sel tid="cohort-gender" v={f.gender} on={set("gender")} ph="Pilih" opts={GENDERS} /></Field>
              <Field label="Julat Umur"><Sel tid="cohort-age" v={f.age_range} on={set("age_range")} ph="Pilih" opts={AGES} /></Field>
            </div>
            <Field label="Peranan / Tahap Jawatan"><Sel tid="cohort-role" v={f.role_level} on={set("role_level")} ph="Pilih peranan" opts={ROLES} /></Field>
            <Field label="Jabatan / Unit (pilihan)"><input data-testid="cohort-dept" className={input} placeholder="cth: Operasi" value={f.department} onChange={set("department")} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tempoh dengan Organisasi"><Sel tid="cohort-years-org" v={f.years_org} on={set("years_org")} ph="Pilih" opts={YEARS} /></Field>
              <Field label="Tempoh dalam Peranan"><Sel tid="cohort-years-role" v={f.years_role} on={set("years_role")} ph="Pilih" opts={YEARS} /></Field>
            </div>
          </div>

          <div className="mt-auto pt-8">
            <button data-testid="cohort-profile-continue" onClick={submit}
              className="group flex w-full items-center justify-center gap-3 rounded-full py-4 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98]"
              style={{ background: PURPLE }}>
              SETERUSNYA <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </GFFrame>
  );
}
